import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useSnackbar } from 'notistack';
import { Link, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Grid from '@material-ui/core/Grid';
import CalculationCommunication from './CalculationCommunication';
import CalculationDetails from './CalculationDetails';
import CalculationItems from './CalculationItems';
import CalculationLogs from './CalculationLogs';
import CalculationProvider from '../../components/CalculationProvider';
import Page from '@afosto/app-components-react/Page';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import { addresses as addressesApi } from '@afosto/contacts/api/Addresses';
import { calculations as calculationsApi } from '@afosto/order/api/Calculations';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { organisations as organisationsApi } from '@afosto/contacts/api/Organisations';
import { oauth as oauthApi } from '@afosto/iam/api/Oauth';
import translations from './translations';

const CalculationView = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:orders:read']);

  const history = useHistory();
  const intl = useIntl();
  const location = useLocation();
  const { calculationID } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [calculation, setCalculation] = useState(null);
  const [isFetchingCalculation, setIsFetchingCalculation] = useState(true);
  const [contact, setContact] = useState(null);
  const [isFetchingContact, setIsFetchingContact] = useState(true);
  const [organisation, setOrganisation] = useState(null);
  const [isFetchingOrganisation, setIsFetchingOrganisation] = useState(true);
  const [billingAddress, setBillingAddress] = useState(null);
  const [isFetchingAddress, setIsFetchingAddress] = useState(true);
  const [client, setClient] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchCalculation = async id => {
      setIsFetchingCalculation(true);

      try {
        const response = await calculationsApi.getCalculation(id);
        const { data = null } = response;

        if (isMounted) {
          setCalculation(data);
        }
      } catch (error) {
        if (isMounted) {
          setCalculation(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingCalculation(false);
        }
      }
    };

    if (calculationID) {
      fetchCalculation(calculationID);
    }

    return () => {
      isMounted = false;
    };
  }, [calculationID]);

  useEffect(() => {
    let isMounted = true;

    const fetchClient = async id => {
      try {
        if (!id) {
          return;
        }

        const response = await oauthApi.getClient(id);
        const { data = null } = response;

        if (isMounted) {
          setClient(data);
        }
      } catch (err) {
        if (isMounted) {
          setClient(null);
        }
      }
    };

    const fetchContact = async contactID => {
      if (!contactID) {
        setIsFetchingContact(false);
        return;
      }

      setIsFetchingContact(true);

      try {
        const response = await contactsApi.getContact(contactID);
        const { data = {} } = response;

        if (isMounted) {
          setContact(data);
        }
      } catch (error) {
        if (isMounted) {
          setContact(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingContact(false);
        }
      }
    };

    const fetchOrganisation = async organisationID => {
      if (!organisationID) {
        setIsFetchingOrganisation(false);
        return;
      }

      setIsFetchingOrganisation(true);

      try {
        if (!organisationID) {
          setIsFetchingOrganisation(false);
          return;
        }

        const response = await organisationsApi.viewOrganisation(organisationID);
        const { data } = response;

        if (isMounted) {
          setOrganisation(data);
        }
      } catch (error) {
        if (isMounted) {
          setOrganisation(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingOrganisation(false);
        }
      }
    };

    /**
     * Fetch addresses by id.
     * @param {string} addressID
     * @returns {Promise<void>}
     */
    const fetchAddress = async addressID => {
      setIsFetchingAddress(true);

      try {
        if (!addressID) {
          setIsFetchingAddress(false);
          return;
        }

        const response = await addressesApi.viewAddress(addressID);

        if (isMounted) {
          setBillingAddress(response?.data ?? null);
        }
      } catch (err) {
        if (isMounted) {
          setBillingAddress(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingAddress(false);
        }
      }
    };

    if (calculation) {
      fetchClient(calculation?.client_id).catch(() => {
        // Catch only.
      });

      fetchContact(calculation?.customer?.contact?.id).catch(() => {
        // Catch only.
      });

      fetchOrganisation(calculation?.customer?.contact?.organisation_id).catch(() => {
        // Catch only.
      });

      fetchAddress(calculation?.customer?.contact?.address_id).catch(() => {
        // Catch only.
      });
    }

    return () => {
      isMounted = false;
    };
  }, [calculation]);

  const handleCreateInvoice = async () => {
    try {
      if (calculation?.items?.length < 100) {
        history.push(`/calculations/${calculation?.id}/invoice`);
        return;
      }

      const response = await calculationsApi.createInvoiceForCalculation(calculation?.id, {
        order_item_ids: ['*'],
      });
      const { data } = response;

      history.push(`/invoices/${data?.id}`);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.invoiceNotCreated), {
        variant: 'error',
      });
    }
  };

  if (!hasPermissions()) {
    return (
      <SkeletonPage fullWidth breadcrumbs={2} subNavigation={3}>
        <UnauthorizedComponent showActions={false} />
      </SkeletonPage>
    );
  }

  if (isFetchingCalculation) {
    return (
      <SkeletonPage fullWidth breadcrumbs={2} subNavigation={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8} xl={9}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={4} xl={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SkeletonPage>
    );
  }

  if (!calculation) {
    return (
      <SkeletonPage fullWidth breadcrumbs={2} subNavigation={3}>
        Calculation not found
      </SkeletonPage>
    );
  }

  const hasAvailableInvoiceItems = calculation?.items.some(item => {
    const availableItems = (item?.order_items ?? []).filter(orderItem => !orderItem.is_invoiced);
    return availableItems.length > 0;
  });

  const secondaryActions = [
    ...(hasAvailableInvoiceItems && hasPermissions(['odr:invoices:read'])
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.createInvoice),
            action: handleCreateInvoice,
            icon: ['fal', 'file-invoice-dollar'],
            shortCut: true,
          },
        ]
      : []),
  ];

  return (
    <CalculationProvider
      value={{
        calculation,
        client,
        contact,
        organisation,
        billingAddress,
        isFetchingAddress,
        isFetchingContact,
        isFetchingOrganisation,
      }}
    >
      <Page
        title={calculation?.number ?? 'Concept'}
        subNavigation={{
          current: location.pathname,
          tabs: [
            {
              label: intl.formatMessage(translations.subNavigation.details),
              path: `/calculations/${calculationID}`,
            },
            {
              label: intl.formatMessage(translations.subNavigation.items),
              path: `/calculations/${calculationID}/items`,
            },
            {
              label: intl.formatMessage(translations.subNavigation.communication),
              path: `/calculations/${calculationID}/communication`,
            },
            {
              label: intl.formatMessage(translations.subNavigation.logs),
              path: `/calculations/${calculationID}/logs`,
            },
          ],
          onChange: (e, value) => history.replace(value),
        }}
        linkComponent={Link}
        secondaryActions={secondaryActions}
        fullWidth
      >
        <Switch>
          <Route component={CalculationItems} path="/calculations/:calculationID/items" />
          <Route component={CalculationLogs} path="/calculations/:calculationID/logs" />
          <Route
            component={CalculationCommunication}
            path="/calculations/:calculationID/communication"
          />
          <Route component={CalculationDetails} path="/calculations/:calculationID" />
        </Switch>
      </Page>
    </CalculationProvider>
  );
};

export default CalculationView;
